import { useEffect, useState } from 'react';
import JsonQuery from 'json-query';
import { uniqueId } from 'lodash';
import { useLocalization } from '../../hooks';

interface MultiSelectFilterProps {
    data: any;
    label: string;
    dataKey: string;
    onChange: (selectedOptions: string[]) => void;
    selectedValue: string;
    multiSelectIconPosition?: 'Left' | 'Right';
    staticDataTabIndex?: number;
    optionsLimit?: number;
    cancelOptionClicks?: number;
    canceledOption?: any;
    clearFilters?: boolean;
}

const MultiSelectFilter = ({ data, label, onChange, dataKey, ...rest }: MultiSelectFilterProps) => {
    const i18n = useLocalization();
    const options: any = dataKey
        ? Array.from(new Set(JsonQuery(dataKey as string, { data }).value || []))
        : [];

    const selectedValue = rest.selectedValue ? rest.selectedValue.split('|') : [];

    const [selectedOptions, setSelectedOptions] = useState<string[]>(
        typeof selectedValue === 'string' ? [selectedValue] : [...selectedValue]
    );

    useEffect(() => {
        if (rest.selectedValue) {
            const newSelectedOptions = rest.selectedValue.split('|');
            setSelectedOptions(newSelectedOptions);
            onChange(newSelectedOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest.selectedValue]);

    useEffect(() => {
        if (selectedOptions && selectedOptions.length > 0) {
            onChange(selectedOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOptions]);

    useEffect(() => {
        if (rest?.canceledOption && selectedOptions?.length > 0) {
            const updatedOptions = [...selectedOptions];
            const selectedIndex = updatedOptions.indexOf(rest?.canceledOption);
            updatedOptions?.splice(selectedIndex, 1);
            setSelectedOptions(updatedOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest?.canceledOption, rest?.cancelOptionClicks]);

    useEffect(() => {
        if (rest?.clearFilters) {
            setSelectedOptions([]);
        }
    }, [rest?.clearFilters]);

    const handleOptionClick = (option: any) => {
        const updatedOptions: string[] = [...selectedOptions];

        const selectedIndex = updatedOptions.indexOf(option);
        if (selectedIndex === -1) {
            setSelectedOptions([...updatedOptions, option]);
        } else {
            updatedOptions.splice(selectedIndex, 1);
            setSelectedOptions(updatedOptions);
        }
        onChange(updatedOptions);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>, option: string) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.click();
            handleOptionClick(option);
        }
    };

    const renderCheckbox = (option: any, id?: string) => {
        return (
            <input
                type="checkbox"
                id={id}
                checked={selectedOptions.indexOf(option) !== -1}
                onChange={() => handleOptionClick(option)}
                onKeyDown={(e) => handleKeyDown(e, option)}
            />
        );
    };

    const labelId = uniqueId('multi-select-label-');
    return (
        <>
            {label ? (
                <div className="multi-select-label" id={labelId} tabIndex={rest.staticDataTabIndex}>
                    {i18n.format(label, data)}
                </div>
            ) : null}
            {options && options?.length > 0
                ? options?.slice(0, rest?.optionsLimit || 3)?.map((option: any) => {
                      const optionId = `${labelId}-${option}`;

                      return (
                          <div key={optionId}>
                              {rest.multiSelectIconPosition === 'Left' &&
                                  renderCheckbox(option, optionId)}
                              <label className="multi-select-label" htmlFor={optionId}>
                                  {option}
                              </label>
                              {rest.multiSelectIconPosition === 'Right' &&
                                  renderCheckbox(option, optionId)}
                          </div>
                      );
                  })
                : null}
        </>
    );
};

export default MultiSelectFilter;
